import React, { useState, useEffect, useCallback, useRef } from 'react';
import stakingABI from '../abi/ADBYTESStaking.json';
import tokenABI from '../abi/ADBYTES.json';
import stakedABI from '../abi/StakedADBYTES.json';
import Toast from './Toast';
import TxBanner from './TxBanner';
import LiveRewardCounter from './LiveRewardCounter';
import {
  formatTokens,
  formatPercentFromBps,
  formatDuration,
  fromWei,
  toWei,
  isWeiLessThan,
} from '../utils/format';
import { baseConfig } from '../config/settings';
import {
  getWalletWeb3,
  readWithFailover,
  getAccount,
  isWalletOnBase,
  getWalletBaseStatus,
  getWalletProvider,
} from '../utils/web3';
import { subscribeWalletEvents } from '../utils/walletProvider';
import { sendContractTransaction, getTxExplorerUrl } from '../utils/transactions';
import { parseTxError } from '../utils/txErrors';

const POLLING_INTERVAL = 30000;
const UNSTAKE_STATUS = { 0: 'None', 1: 'Pending', 2: 'Claimable', 3: 'Expired' };

const IDLE_TX = { phase: 'idle', message: '', action: '', txHash: null };

const StakingPanel = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isTxActive, setIsTxActive] = useState(false);
  const [txState, setTxState] = useState(IDLE_TX);
  const txActiveRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [wrongNetwork, setWrongNetwork] = useState(false);
  const [account, setAccount] = useState('');
  const [stakingAddress, setStakingAddress] = useState('');
  const [stakedTokenAddress, setStakedTokenAddress] = useState('');

  const [networkApy, setNetworkApy] = useState('0');
  const [totalStaked, setTotalStaked] = useState('0');
  const [treasuryBalance, setTreasuryBalance] = useState('0');
  const [dailyEmission, setDailyEmission] = useState('0');
  const [stakingEnabled, setStakingEnabled] = useState(false);
  const [cooldownPeriod, setCooldownPeriod] = useState(0);
  const [claimWindow, setClaimWindow] = useState(0);

  const [walletBalance, setWalletBalance] = useState('0');
  const [stakedBalance, setStakedBalance] = useState('0');
  const [pendingRewards, setPendingRewards] = useState('0');

  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [unstakeRequest, setUnstakeRequest] = useState(null);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));

  const [toast, setToast] = useState({ visible: false, message: '', type: 'success', linkHref: null });
  const [activeTab, setActiveTab] = useState('stake');
  const didAutoTab = useRef(false);

  const showToast = (type, message, linkHref = null) => {
    setToast({ visible: true, type, message, linkHref });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), type === 'error' ? 5000 : 4000);
  };

  const loadData = useCallback(async (isRefresh = false) => {
    if (txActiveRef.current) return;

    if (isRefresh) setRefreshing(true);

    const stakingAddr = baseConfig.stakingContractAddress;
    const tokenAddr = baseConfig.contractAddress;

    try {
      const walletWeb3 = getWalletWeb3();
      const userAccount = walletWeb3 ? await getAccount(walletWeb3) : null;

      const { readWeb3, stats, config, stakedAddr, user } = await readWithFailover(
        async (web3) => {
          const stakingContract = new web3.eth.Contract(stakingABI, stakingAddr);
          const tokenContract = new web3.eth.Contract(tokenABI, tokenAddr);

          const [statsRes, configRes, stakedAddrRes] = await Promise.all([
            stakingContract.methods.getStats().call(),
            stakingContract.methods.getConfig().call(),
            stakingContract.methods.stakedToken().call(),
          ]);

          let userRes = null;
          if (userAccount) {
            const [pending, effectiveStaked, walletBal, request] = await Promise.all([
              stakingContract.methods.pendingRewards(userAccount).call(),
              stakingContract.methods.effectiveStakedBalance(userAccount).call(),
              tokenContract.methods.balanceOf(userAccount).call(),
              stakingContract.methods.getUnstakeRequest(userAccount).call(),
            ]);
            userRes = { pending, effectiveStaked, walletBal, request };
          }

          return {
            readWeb3: web3,
            stats: statsRes,
            config: configRes,
            stakedAddr: stakedAddrRes,
            user: userRes,
          };
        }
      );

      setStakingAddress(stakingAddr);
      setReady(true);
      setLoadError('');
      setStakedTokenAddress(stakedAddr);
      setNetworkApy(stats.estimatedAPYBps);
      setTotalStaked(fromWei(stats.totalStaked, readWeb3));
      setTreasuryBalance(fromWei(stats.treasuryBalance, readWeb3));
      setDailyEmission(fromWei(stats.dailyEmission, readWeb3));
      setStakingEnabled(config.stakingEnabled);
      setCooldownPeriod(Number(config.cooldown));
      setClaimWindow(Number(config.claimWindowDuration));

      if (userAccount && user) {
        setAccount(userAccount);
        const { onBase, known } = await getWalletBaseStatus();
        setWrongNetwork(known && !onBase);

        setPendingRewards(fromWei(user.pending, readWeb3));
        setStakedBalance(fromWei(user.effectiveStaked, readWeb3));
        setWalletBalance(fromWei(user.walletBal, readWeb3));

        const status = Number(user.request.status);
        if (Number(user.request.amount) > 0) {
          setUnstakeRequest({
            amount: fromWei(user.request.amount, readWeb3),
            requestTime: Number(user.request.requestTime),
            cooldownEnd: Number(user.request.cooldownEnd),
            claimDeadline: Number(user.request.claimDeadline),
            status,
            statusLabel: UNSTAKE_STATUS[status] || 'Unknown',
          });
        } else {
          setUnstakeRequest(null);
        }
      } else {
        setAccount('');
        setWrongNetwork(false);
        setPendingRewards('0');
        setStakedBalance('0');
        setWalletBalance('0');
        setUnstakeRequest(null);
      }
    } catch (error) {
      console.error('Failed to load staking data:', error);
      setReady(false);
      setLoadError('Could not load staking data. Please try again.');
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData(false);
    // Skip polling while the tab is hidden to avoid hammering the public RPC.
    const poll = setInterval(() => {
      if (!document.hidden) loadData(true);
    }, POLLING_INTERVAL);
    const clock = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    const onVisible = () => {
      if (!document.hidden) loadData(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadData]);

  useEffect(() => {
    if (unstakeRequest && !didAutoTab.current) {
      setActiveTab('unstake');
      didAutoTab.current = true;
    }
  }, [unstakeRequest]);

  // When a cooldown or claim window crosses its boundary, refresh once
  // immediately so the on-chain status flips without waiting for the next poll.
  const boundaryRefreshed = useRef(0);
  useEffect(() => {
    if (!unstakeRequest) return;
    if (
      unstakeRequest.status === 1 &&
      now >= unstakeRequest.cooldownEnd &&
      boundaryRefreshed.current !== unstakeRequest.cooldownEnd
    ) {
      boundaryRefreshed.current = unstakeRequest.cooldownEnd;
      loadData(true);
    } else if (
      unstakeRequest.status === 2 &&
      now > unstakeRequest.claimDeadline &&
      boundaryRefreshed.current !== unstakeRequest.claimDeadline
    ) {
      boundaryRefreshed.current = unstakeRequest.claimDeadline;
      loadData(true);
    }
  }, [now, unstakeRequest, loadData]);

  useEffect(() => {
    return subscribeWalletEvents({
      onAccountsChanged: () => loadData(true),
      onChainChanged: () => loadData(true),
    });
  }, [loadData]);

  const runSteps = async (actionLabel, steps, { onSuccess } = {}) => {
    txActiveRef.current = true;
    setIsTxActive(true);
    setTxState({ phase: 'signing', message: 'Confirm in your wallet…', action: actionLabel, txHash: null });

    const tracker = { lastTxHash: null };
    const totalSteps = steps.length;

    const makeStatusHandler = (stepNum) => (status) => {
      if (status.txHash) tracker.lastTxHash = status.txHash;
      const isMulti = totalSteps > 1;
      setTxState({
        phase: status.phase,
        message:
          status.phase === 'signing' && isMulti
            ? `Step ${stepNum} of ${totalSteps}: ${status.message || 'Confirm in wallet…'}`
            : status.message,
        action: actionLabel,
        txHash: status.txHash || tracker.lastTxHash,
      });
    };

    try {
      for (let i = 0; i < steps.length; i++) {
        await steps[i](makeStatusHandler(i + 1));
      }

      const explorerUrl = getTxExplorerUrl(tracker.lastTxHash);
      showToast('success', `${actionLabel} confirmed!`, explorerUrl);
      onSuccess?.();
      await loadData(true);

      setTimeout(() => setTxState(IDLE_TX), 6000);
    } catch (error) {
      const message = parseTxError(error, `${actionLabel} failed`);
      setTxState({
        phase: 'error',
        message,
        action: actionLabel,
        txHash: tracker.lastTxHash,
      });
      showToast('error', message, getTxExplorerUrl(tracker.lastTxHash));
    } finally {
      txActiveRef.current = false;
      setIsTxActive(false);
    }
  };

  // `makeSend` is a thunk so the transaction is only dispatched when the step
  // actually runs — NOT when the steps array is built. This is essential for
  // multi-step flows (approve then stake), so the second tx isn't submitted
  // before the first is confirmed.
  const sendStep = (getMethod, labels, gasFallback) => (onStatus) =>
    sendContractTransaction(getMethod(), { from: account, gasFallback, web3: getWalletWeb3() }, {
      onStatus: (s) =>
        onStatus({
          ...s,
          message:
            s.phase === 'signing'
              ? labels.signing
              : s.phase === 'pending'
              ? labels.pending
              : s.message,
        }),
      successMessage: labels.success,
    });

  const requireBaseNetwork = async () => {
    if (await isWalletOnBase()) return true;
    showToast('error', 'Switch your wallet to Base Mainnet to continue.');
    return false;
  };

  const handleStake = async () => {
    const web3 = getWalletWeb3();
    if (!web3 || !stakeAmount || Number(stakeAmount) <= 0) return;
    if (!(await requireBaseNetwork())) return;
    if (Number(stakeAmount) > Number(walletBalance)) {
      showToast('error', 'Amount exceeds your ADBYTES balance.');
      return;
    }

    let amountWei;
    try {
      amountWei = toWei(stakeAmount, web3);
    } catch {
      showToast('error', 'Invalid amount — use up to 18 decimal places.');
      return;
    }

    const tokenContract = new web3.eth.Contract(tokenABI, baseConfig.contractAddress);
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);

    const steps = [];

    const allowance = await tokenContract.methods.allowance(account, stakingAddress).call();
    if (isWeiLessThan(allowance, amountWei)) {
      steps.push(
        sendStep(
          () => tokenContract.methods.approve(stakingAddress, amountWei),
          {
            signing: 'Approve ADBYTES spending in your wallet',
            pending: 'Waiting for approval confirmation…',
            success: 'Approval confirmed',
          },
          100_000
        )
      );
    }

    steps.push(
      sendStep(
        () => stakingContract.methods.stake(amountWei),
        {
          signing: 'Confirm stake in your wallet',
          pending: 'Staking ADBYTES…',
          success: 'Stake confirmed',
        },
        400_000
      )
    );

    await runSteps('Stake', steps, { onSuccess: () => setStakeAmount('') });
  };

  const handleHarvest = async () => {
    if (!(await requireBaseNetwork())) return;
    const web3 = getWalletWeb3();
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);
    await runSteps('Harvest', [
      sendStep(
        () => stakingContract.methods.harvest(),
        {
          signing: 'Confirm harvest in your wallet',
          pending: 'Harvesting rewards…',
          success: 'Rewards harvested',
        },
        250_000
      ),
    ]);
  };

  const handleRestake = async () => {
    if (!(await requireBaseNetwork())) return;
    const web3 = getWalletWeb3();
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);
    await runSteps('Restake', [
      sendStep(
        () => stakingContract.methods.restake(),
        {
          signing: 'Confirm restake in your wallet',
          pending: 'Compounding rewards…',
          success: 'Rewards restaked',
        },
        300_000
      ),
    ]);
  };

  const handleRequestUnstake = async () => {
    const web3 = getWalletWeb3();
    if (!unstakeAmount || Number(unstakeAmount) <= 0) return;
    if (!(await requireBaseNetwork())) return;
    if (Number(unstakeAmount) > Number(stakedBalance)) {
      showToast('error', 'Amount exceeds your staked balance.');
      return;
    }

    let amountWei;
    try {
      amountWei = toWei(unstakeAmount, web3);
    } catch {
      showToast('error', 'Invalid amount — use up to 18 decimal places.');
      return;
    }

    const stakedContract = new web3.eth.Contract(stakedABI, stakedTokenAddress);
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);

    const steps = [];
    const allowance = await stakedContract.methods.allowance(account, stakingAddress).call();
    if (isWeiLessThan(allowance, amountWei)) {
      steps.push(
        sendStep(
          () => stakedContract.methods.approve(stakingAddress, amountWei),
          {
            signing: 'Approve stADBYTES spending in your wallet',
            pending: 'Waiting for approval confirmation…',
            success: 'Approval confirmed',
          },
          100_000
        )
      );
    }

    steps.push(
      sendStep(
        () => stakingContract.methods.requestUnstake(amountWei),
        {
          signing: 'Confirm unstake request in your wallet',
          pending: 'Submitting unstake request…',
          success: 'Unstake request submitted',
        },
        400_000
      )
    );

    await runSteps('Unstake request', steps, {
      onSuccess: () => {
        setUnstakeAmount('');
        setActiveTab('unstake');
      },
    });
  };

  const handleCompleteUnstake = async () => {
    if (!(await requireBaseNetwork())) return;
    const web3 = getWalletWeb3();
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);
    await runSteps('Unstake', [
      sendStep(
        () => stakingContract.methods.completeUnstake(),
        {
          signing: 'Confirm unstake in your wallet',
          pending: 'Returning ADBYTES to your wallet…',
          success: 'Unstake complete',
        },
        300_000
      ),
    ]);
  };

  const handleClearExpired = async () => {
    if (!(await requireBaseNetwork())) return;
    const web3 = getWalletWeb3();
    const stakingContract = new web3.eth.Contract(stakingABI, stakingAddress);
    await runSteps('Return tokens', [
      sendStep(
        () => stakingContract.methods.clearExpiredUnstake(),
        {
          signing: 'Confirm return in your wallet',
          pending: 'Returning stADBYTES to your wallet…',
          success: 'Tokens returned',
        },
        250_000
      ),
    ]);
  };

  const addStakedTokenToWallet = async () => {
    const walletProvider = getWalletProvider();
    if (!walletProvider || !stakedTokenAddress) return;
    try {
      await walletProvider.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: { address: stakedTokenAddress, symbol: 'stADBYTES', decimals: 18 },
        },
      });
      showToast('success', 'stADBYTES added to wallet');
    } catch (error) {
      if (!parseTxError(error).includes('cancelled')) {
        showToast('error', 'Could not add token to wallet');
      }
    }
  };

  if (initialLoading) {
    return (
      <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl" />
            ))}
          </div>
          <div className="h-32 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!ready && !initialLoading) {
    return (
      <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Staking</h3>
        <p className="text-sm text-gray-600 mb-4">
          {loadError || 'Unable to load staking data right now.'}
        </p>
        <button
          type="button"
          onClick={() => loadData(false)}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const hasRewards = Number(pendingRewards) > 0;
  const treasuryDepleted = hasRewards && Number(treasuryBalance) <= 0;
  const stakeTooHigh = stakeAmount && Number(stakeAmount) > Number(walletBalance);
  const unstakeTooHigh = unstakeAmount && Number(unstakeAmount) > Number(stakedBalance);
  const buttonsDisabled = isTxActive || wrongNetwork;

  const cooldownRemaining =
    unstakeRequest?.status === 1 ? Math.max(0, unstakeRequest.cooldownEnd - now) : 0;
  const claimRemaining =
    unstakeRequest?.status === 2 ? Math.max(0, unstakeRequest.claimDeadline - now) : 0;

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-gray-900">Staking</h3>
            {refreshing && !isTxActive && (
              <span className="text-xs text-gray-400 animate-pulse">Updating…</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Stake ADBYTES to earn daily ADBYTES rewards. You receive stADBYTES as a receipt for your staked position.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!stakingEnabled && (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
              Emissions paused
            </span>
          )}
          <button
            type="button"
            onClick={addStakedTokenToWallet}
            disabled={buttonsDisabled}
            className="px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
          >
            Add stADBYTES
          </button>
        </div>
      </div>

      {wrongNetwork && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          Your wallet is not on Base Mainnet. You can view staking stats below, but switch to Base to stake, harvest, or unstake.
        </div>
      )}

      {loadError && ready && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
          <p className="text-sm text-amber-800">{loadError}</p>
          <button
            type="button"
            onClick={() => loadData(false)}
            className="text-xs font-semibold text-amber-800 underline whitespace-nowrap"
          >
            Retry
          </button>
        </div>
      )}

      <TxBanner txState={txState} />

      {treasuryDepleted && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          You have accrued rewards but the reward treasury is currently empty. Harvest and restake
          will succeed once the pool is funded.
        </div>
      )}

      {/* Overview stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
          <div className="text-xs text-green-700 font-medium mb-1">Staking APY</div>
          <div className="text-2xl font-bold text-gray-900">{formatPercentFromBps(networkApy)}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
          <div className="text-xs text-blue-700 font-medium mb-1">Your Staked</div>
          <div className="text-2xl font-bold text-gray-900">{formatTokens(stakedBalance)}</div>
          <div className="text-xs text-gray-500 mt-1">stADBYTES</div>
        </div>
        <div
          className={`bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-4 border transition-shadow duration-300 ${
            Number(pendingRewards) > 0 ? 'border-purple-200 shadow-sm' : 'border-purple-100'
          }`}
        >
          <div className="text-xs text-purple-700 font-medium mb-1">Pending Rewards</div>
          <div className="text-2xl font-bold">
            <LiveRewardCounter
              pendingRewards={pendingRewards}
              stakedBalance={stakedBalance}
              totalStaked={totalStaked}
              dailyEmission={dailyEmission}
              decimals={3}
            />
          </div>
          <div className="text-xs text-gray-500 mt-1">ADBYTES</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 text-center">
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <div className="text-xs text-gray-500">Total Staked</div>
          <div className="text-sm font-bold text-gray-900">{formatTokens(totalStaked)}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <div className="text-xs text-gray-500">Daily Emission</div>
          <div className="text-sm font-bold text-gray-900">{formatTokens(dailyEmission)}</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
          <div className="text-xs text-gray-500">Reward Treasury</div>
          <div className={`text-sm font-bold ${Number(treasuryBalance) <= 0 ? 'text-amber-600' : 'text-gray-900'}`}>
            {formatTokens(treasuryBalance)}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <button
          type="button"
          disabled={buttonsDisabled || !hasRewards}
          onClick={handleHarvest}
          className="flex-1 min-w-[140px] bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Harvest Rewards
        </button>
        <button
          type="button"
          disabled={buttonsDisabled || !hasRewards || !stakingEnabled}
          onClick={handleRestake}
          className="flex-1 min-w-[140px] bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Restake (Compound)
        </button>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-200">
          {['stake', 'unstake'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              disabled={buttonsDisabled}
              className={`flex-1 py-3 text-sm font-semibold capitalize transition-colors disabled:opacity-50 ${
                activeTab === tab
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab}
              {tab === 'unstake' && unstakeRequest && (
                <span className="ml-1.5 inline-block w-2 h-2 rounded-full bg-orange-500" />
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          {activeTab === 'stake' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="stake-amount" className="text-xs font-semibold text-gray-600 mb-1 block">
                  Amount to stake
                </label>
                <div className="flex gap-2">
                  <input
                    id="stake-amount"
                    type="number"
                    min="0"
                    step="any"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    placeholder="0.00"
                    disabled={buttonsDisabled}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={() => setStakeAmount(walletBalance)}
                    disabled={buttonsDisabled || Number(walletBalance) <= 0}
                    className="px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                  >
                    Max
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Available: {formatTokens(walletBalance)} ADBYTES
                </p>
                {stakeTooHigh && (
                  <p className="text-xs text-red-600 mt-1">Exceeds your available balance.</p>
                )}
              </div>
              <button
                type="button"
                disabled={
                  buttonsDisabled ||
                  !stakingEnabled ||
                  !stakeAmount ||
                  Number(stakeAmount) <= 0 ||
                  stakeTooHigh
                }
                onClick={handleStake}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Stake ADBYTES
              </button>
              {!stakingEnabled && (
                <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                  New staking is paused while emissions are set to zero. You can still unstake and
                  harvest existing rewards.
                </p>
              )}
            </div>
          )}

          {activeTab === 'unstake' && (
            <div className="space-y-4">
              {unstakeRequest ? (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-gray-700">Active unstake request</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        unstakeRequest.status === 2
                          ? 'bg-green-100 text-green-800'
                          : unstakeRequest.status === 1
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {unstakeRequest.statusLabel}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatTokens(unstakeRequest.amount)} ADBYTES
                  </div>

                  {unstakeRequest.status === 1 && (
                    <>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-500 h-2 rounded-full transition-all duration-1000"
                          style={{
                            width: `${(() => {
                              const total =
                                unstakeRequest.cooldownEnd - unstakeRequest.requestTime;
                              if (total <= 0) return 100;
                              return Math.min(
                                100,
                                ((now - unstakeRequest.requestTime) / total) * 100
                              );
                            })()}%`,
                          }}
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        {cooldownRemaining > 0 ? (
                          <>
                            Cooldown ends in{' '}
                            <span className="font-semibold">{formatDuration(cooldownRemaining)}</span>
                          </>
                        ) : (
                          <span className="font-semibold text-green-700">
                            Cooldown complete — finalizing…
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Your stADBYTES is in escrow. Rewards pause on this amount during cooldown.
                      </p>
                    </>
                  )}

                  {unstakeRequest.status === 2 && (
                    <>
                      <p className="text-sm text-gray-600">
                        Ready to complete! Claim within{' '}
                        <span className="font-semibold text-green-700">
                          {formatDuration(claimRemaining)}
                        </span>
                      </p>
                      <button
                        type="button"
                        disabled={buttonsDisabled}
                        onClick={handleCompleteUnstake}
                        className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 transition-all"
                      >
                        Complete Unstake
                      </button>
                    </>
                  )}

                  {unstakeRequest.status === 3 && (
                    <>
                      <p className="text-sm text-gray-600">
                        Claim window expired. Return your stADBYTES to try again.
                      </p>
                      <button
                        type="button"
                        disabled={buttonsDisabled}
                        onClick={handleClearExpired}
                        className="w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-gray-600 hover:to-gray-700 disabled:opacity-50 transition-all"
                      >
                        Return to Wallet
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <label htmlFor="unstake-amount" className="text-xs font-semibold text-gray-600 mb-1 block">
                      Amount to unstake
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="unstake-amount"
                        type="number"
                        min="0"
                        step="any"
                        value={unstakeAmount}
                        onChange={(e) => setUnstakeAmount(e.target.value)}
                        placeholder="0.00"
                        disabled={buttonsDisabled}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={() => setUnstakeAmount(stakedBalance)}
                        disabled={buttonsDisabled || Number(stakedBalance) <= 0}
                        className="px-3 py-2 text-xs font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 disabled:opacity-50"
                      >
                        Max
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Staked: {formatTokens(stakedBalance)} stADBYTES
                    </p>
                    {unstakeTooHigh && (
                      <p className="text-xs text-red-600 mt-1">Exceeds your staked balance.</p>
                    )}
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                    Unstaking requires a {formatDuration(cooldownPeriod)} cooldown, then a{' '}
                    {formatDuration(claimWindow)} window to complete. Tokens are locked during
                    cooldown and stop earning rewards.
                  </div>
                  <button
                    type="button"
                    disabled={
                      buttonsDisabled ||
                      !unstakeAmount ||
                      Number(unstakeAmount) <= 0 ||
                      unstakeTooHigh
                    }
                    onClick={handleRequestUnstake}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3 rounded-lg font-semibold text-sm shadow-md hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Request Unstake
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {stakingAddress && (
        <div className="mt-4 text-center">
          <a
            href={`https://basescan.org/address/${stakingAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 hover:text-blue-600 transition-colors"
          >
            View staking contract on BaseScan ↗
          </a>
        </div>
      )}

      <Toast
        message={toast.message}
        visible={toast.visible}
        type={toast.type}
        linkHref={toast.linkHref}
      />
    </div>
  );
};

export default StakingPanel;
